import {
  buildPath,
  type ChapterProposal,
  type ChapterProposalReady,
  type ManuscriptNode,
  ProposalDraftSchema,
} from "@contract";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { api } from "../../api/fetcher";
import { useJobRetry } from "../../api/hooks";
import { queryKeys } from "../../api/keys";
import { Button } from "../../components/control/Button";
import { Select } from "../../components/control/Select";
import { TextInput } from "../../components/control/TextInput";
import { Card } from "../../components/data/Card";
import { Banner } from "../../components/feedback/Banner";
import { ErrorBanner } from "../../components/feedback/ErrorBanner";
import { Num } from "../../components/format/Num";
import { useGuidance } from "../../components/guidance";
import { CheckIcon, CloseIcon } from "../../components/icons";
import { RespendConfirmDialog } from "../../components/overlay/RespendConfirmDialog";
import { DraftActions } from "../../drafts/DraftActions";
import { useWorkspaceDraft } from "../../drafts/useWorkspaceDraft";
import { useBookDetail } from "../book/bookContext";
import {
  type ChapterDraft,
  type DraftChapter,
  fromProposal,
  mergeUp,
  normalizeCalibration,
  wireDraft,
} from "./draft";
import { SplitDialog } from "./SplitDialog";

export function ChaptersScreen() {
  const { t } = useTranslation("chapters");
  const book = useBookDetail();
  const retry = useJobRetry();
  const parsePending =
    book.state === "uploaded" ||
    book.activeJobs.some(
      (job) => job.kind === "parse" && (job.status === "queued" || job.status === "running"),
    );
  const parseFailed = book.activeJobs.some(
    (job) => job.kind === "parse" && job.manualRetryRequired,
  );
  const query = useQuery({
    queryKey: queryKeys.proposal(book.id),
    queryFn: () => api<ChapterProposal>(buildPath("/books/:id/proposal", { id: book.id })),
    enabled: !parsePending && !parseFailed,
    retry: false,
    refetchInterval: (q) => (q.state.data?.status === "ready" ? false : 2000),
  });
  if (parseFailed) return <p className="text-ink-2">{t("parsingFailed")}</p>;
  if (parsePending || query.isPending)
    return (
      <div className="w-full">
        <h1 className="mb-4 font-serif text-title font-semibold">{t("title")}</h1>
        <Banner tone="working">{parsePending ? t("parsing") : t("proposing")}</Banner>
      </div>
    );
  if (!query.data)
    return (
      <div className="w-full">
        <ErrorBanner
          error={query.error}
          action={
            <Button variant="quiet" onClick={() => void query.refetch()}>
              {t("retry")}
            </Button>
          }
        />
      </div>
    );
  if (query.data.status !== "ready")
    return (
      <div className="w-full">
        <h1 className="mb-4 font-serif text-title font-semibold">{t("title")}</h1>
        <Banner
          tone={query.data.status === "failed" ? "danger" : "working"}
          action={
            query.data.status === "failed" && query.data.job ? (
              <Button
                busy={retry.isPending}
                onClick={() => {
                  const job = query.data?.status === "failed" ? query.data.job : null;
                  if (job) retry.mutate(job.id, { onSuccess: () => void query.refetch() });
                }}
              >
                {t("retry")}
              </Button>
            ) : undefined
          }
        >
          {query.data.status === "failed" ? t("proposalFailed") : t("proposing")}
        </Banner>
        <ErrorBanner error={retry.error || query.error} />
        <RespendConfirmDialog state={retry.respend} />
      </div>
    );
  return <ProposalEditor key={book.id} proposal={query.data} readError={query.error} />;
}

function ProposalEditor({
  proposal,
  readError,
}: {
  proposal: ChapterProposalReady;
  readError: unknown;
}) {
  const { t } = useTranslation("chapters");

  const book = useBookDetail();
  const navigate = useNavigate();
  const workspace = useWorkspaceDraft(
    book.id,
    "chapters",
    fromProposal(proposal),
    proposal.editable,
  );
  const draft = workspace.value;
  const setDraft = workspace.setValue;
  const [saved, setSaved] = useState(false);
  const [splitAt, setSplitAt] = useState<number | null>(null);
  const reportRows = [
    { field: "words", label: t("report.words") },
    { field: "headings", label: t("report.headings") },
    { field: "footnotes", label: t("report.footnotes") },
    { field: "tables", label: t("report.tables") },
    { field: "figures", label: t("report.figures") },
  ] as const;
  const editable = proposal.editable && book.confirmedAt === null;
  // A blocked proceed speaks: the fields highlight, and the first one takes
  // focus, so an empty title is found rather than inferred from a grey button.
  const guide = useGuidance();
  const titleError = (row: DraftChapter) =>
    (guide.attempted || workspace.dirty) && row.title.trim().length === 0
      ? t("errors.titleRequired")
      : undefined;
  const calibrationError =
    (guide.attempted || workspace.dirty) &&
    !draft.chapters.some((row) => row.nodeStart === draft.calibrationStart && !row.excluded)
      ? t("errors.calibrationRequired")
      : undefined;
  const update = (next: ChapterDraft) => {
    setDraft(normalizeCalibration(next));
    setSaved(false);
  };
  const save = { isPending: workspace.saving, error: workspace.error };
  const confirm = { isPending: workspace.applying, error: workspace.error };
  const busy = workspace.applying || !workspace.loaded;
  const validDraft = ProposalDraftSchema.safeParse(wireDraft(draft)).success;
  const editRow = (index: number, patch: Partial<ChapterDraft["chapters"][number]>) =>
    update({
      ...draft,
      chapters: draft.chapters.map((row, at) => (at === index ? { ...row, ...patch } : row)),
    });
  const split = (node: ManuscriptNode) => {
    if (splitAt === null) return;
    const original = draft.chapters[splitAt];
    if (!original) return;
    const chapters = [...draft.chapters];
    chapters[splitAt] = { ...original, wordCount: null };
    chapters.splice(splitAt + 1, 0, {
      ...original,
      nodeStart: node.ordinal,
      title: node.text.trim().slice(0, 300) || t("newChapter"),
      firstLine: node.text,
      wordCount: null,
      proposalExcluded: false,
      reason: null,
    });
    update({ ...draft, chapters });
    setSplitAt(null);
  };
  const splitRow = splitAt === null ? null : draft.chapters[splitAt];
  return (
    <div className="grid min-w-0 flex-1 grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
      <section className="min-w-0 rounded-md border border-line bg-surface">
        <header className="space-y-2 border-b border-line px-5 py-5">
          <h1 className="font-serif text-title font-semibold">{t("title")}</h1>
          <p className="text-ink-2">{t("description")}</p>
        </header>
        <ErrorBanner error={save.error || confirm.error || readError} />
        {!editable ? <Banner tone="info">{t("locked")}</Banner> : null}
        {saved ? <Banner tone="info">{t("saved")}</Banner> : null}
        <ol className="divide-y divide-line-soft">
          {draft.chapters.map((row, index) => (
            <li
              key={row.nodeStart}
              data-excluded={row.excluded}
              className={`grid min-w-0 grid-cols-[28px_minmax(0,1fr)] gap-3 px-5 py-3 ${row.excluded ? "bg-ground" : "bg-surface"}`}
            >
              <span className="pt-2 font-mono text-data text-ink-3">{index + 1}</span>
              <div className="min-w-0 space-y-2">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 text-data font-medium ${row.excluded ? "border-dashed border-line-strong text-ink-2" : "border-ok/30 bg-ok-soft text-ok"}`}
                >
                  {row.excluded ? <CloseIcon size={14} /> : <CheckIcon size={14} />}
                  {row.excluded ? t("excludedStatus") : t("includedStatus")}
                </span>
                <TextInput
                  className={
                    row.excluded
                      ? "[&_input]:text-ink-2 [&_input]:line-through [&_input]:decoration-ink-3"
                      : undefined
                  }
                  aria-label={t("chapterTitle", { position: index + 1 })}
                  value={row.title}
                  maxLength={300}
                  disabled={!editable || busy}
                  error={titleError(row)}
                  onChange={(e) => editRow(index, { title: e.target.value })}
                />
                {row.firstLine ? (
                  <p className="truncate font-serif text-book-sm text-ink-2" title={row.firstLine}>
                    {row.firstLine}
                  </p>
                ) : null}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <span className="font-mono text-data text-ink-3">
                    {row.wordCount === null
                      ? t("countsOnSave")
                      : t("words", { count: row.wordCount })}
                  </span>
                  {row.proposalExcluded ? (
                    <span className="text-data text-ink-2" title={row.reason ?? undefined}>
                      {t("proposedExcluded")}
                    </span>
                  ) : null}
                  {editable ? (
                    <div className="flex flex-wrap gap-1">
                      <Button
                        variant="quiet"
                        size="sm"
                        disabled={index === 0 || busy}
                        onClick={() => update(mergeUp(draft, index))}
                        aria-label={t("mergeNamed", { title: row.title })}
                      >
                        {t("merge")}
                      </Button>
                      <Button
                        variant="quiet"
                        size="sm"
                        disabled={
                          busy ||
                          (draft.chapters[index + 1]?.nodeStart ?? proposal.nodeCount) -
                            row.nodeStart <
                            2
                        }
                        onClick={() => setSplitAt(index)}
                        aria-label={t("splitNamed", { title: row.title })}
                      >
                        {t("split")}
                      </Button>
                      <Button
                        variant={row.excluded ? "secondary" : "quiet"}
                        size="sm"
                        disabled={busy}
                        onClick={() => editRow(index, { excluded: !row.excluded })}
                        aria-label={
                          row.excluded
                            ? t("includeNamed", { title: row.title })
                            : t("excludeNamed", { title: row.title })
                        }
                      >
                        {row.excluded ? t("include") : t("exclude")}
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ol>
        {proposal.notes ? (
          <p className="border-t border-line px-5 py-3 text-ink-2">{proposal.notes}</p>
        ) : null}
        <div className="space-y-4 border-t border-line px-5 py-4">
          <Select
            label={t("calibration")}
            hint={t("calibrationHelp")}
            value={String(draft.calibrationStart)}
            disabled={!editable || busy}
            error={calibrationError}
            options={draft.chapters
              .filter((row) => !row.excluded)
              .map((row) => ({ value: String(row.nodeStart), label: row.title }))}
            onChange={(e) => update({ ...draft, calibrationStart: Number(e.target.value) })}
          />
          {!validDraft ? (
            <p role="alert" className="text-danger">
              {t("invalidDraft")}
            </p>
          ) : null}
          <p className="text-ink-2">{t("confirmHelp")}</p>
          {editable ? (
            <DraftActions
              draft={workspace}
              label={t("nav:workspace.confirmChapters")}
              always
              disabled={!validDraft || !book.metadataConfirmedAt}
              onApply={() =>
                void workspace
                  .apply()
                  .then(() => navigate(`/books/${book.id}/overview`))
                  .catch(() => {})
              }
            >
              {!book.metadataConfirmedAt ? (
                <p>
                  <a className="text-accent underline" href={`/books/${book.id}/overview`}>
                    {t("nav:workspace.confirmMetadata")}
                  </a>
                </p>
              ) : null}
            </DraftActions>
          ) : (
            <Button onClick={() => void navigate(`/books/${book.id}/style-sheet`)}>
              {t("openStyleSheet")}
            </Button>
          )}
        </div>
      </section>
      <aside className="min-w-0 space-y-4">
        <Card label={t("book")}>
          <p className="break-words font-medium">{book.title}</p>
          <p className="mt-2 whitespace-pre-wrap break-words">{book.authorName}</p>
          <a
            className="mt-3 inline-block text-accent underline"
            href={`/books/${book.id}/overview`}
          >
            {t("nav:workspace.confirmMetadata")}
          </a>
        </Card>
        <Card label={t("parseReport")}>
          <dl className="space-y-2 text-data">
            <div className="flex flex-col gap-1">
              <dt className="text-ink-3">{t("source")}</dt>
              <dd className="break-all">{book.source.filename}</dd>
            </div>
            {reportRows.map(({ field, label }) => (
              <div key={field} className="flex justify-between gap-3">
                <dt className="text-ink-3">{label}</dt>
                <dd className="font-mono">
                  <Num value={proposal.parseReport[field]} />
                </dd>
              </div>
            ))}
          </dl>
        </Card>
      </aside>
      {splitRow && splitAt !== null ? (
        <SplitDialog
          bookId={book.id}
          from={splitRow.nodeStart}
          to={draft.chapters[splitAt + 1]?.nodeStart ?? proposal.nodeCount}
          onClose={() => setSplitAt(null)}
          onSplit={split}
        />
      ) : null}
    </div>
  );
}
